/**
 * Player Buttons
 * @typedef {Object} PLAYER_BUTTONS collection of player buttons
 * @property {HTMLButtonElement} playPauseBtn play/pause button
 * @property {HTMLButtonElement} theaterBtn display theater mode button
 * @property {HTMLButtonElement} fullScreenBtn display full screen mode button
 * @property {HTMLButtonElement} minPlayerBtn display mini player mode button
 * @property {HTMLButtonElement} muteBtn mute button
 * @property {HTMLButtonElement} captionsBtn captions button
 * @property {HTMLButtonElement} speedBtn speed button
 * @property {HTMLButtonElement} replayBtn replay btn
 */
/**
 * Player containers
 * @typedef {Object} PLAYER_CONTAINERS of div containers
 * @property {HTMLVideoElement} volumeSlider  render volumen slider
 * @property {HTMLVideoElement} currentTimeElement display current time
 * @property {HTMLVideoElement} totalTimeElement display total time
 * @property {HTMLVideoElement} timeLineContainer render time line
 * @property {HTMLVideoElement} previewImg  render smal preview images
 * @property {HTMLVideoElement} thumbnailImg render thumbnail image
 * @property {HTMLVideoElement} thumbIndicator render thumb indicator
 * @property {HTMLVideoElement} videoContainer video container
 * @property {HTMLVideoElement} playIcon play Icon
 */
/**
 * video player options
 * @typedef {Object} VIDEO_PLAYER_OPTIONS VideoPlayer_JS parameters
 * @property {string} selector query selector (main video container)
 * @property {Boolean} mandatory restrict to play withouth shortcuts
 * @property {Number} numberOfPreviewImages number of preview images 
 * @property {Number} previewImagesPathAndFileName path and file name of preview images (must be a collection of jpg images)
 * @property {string} chaptersVideoSelector query selector for chapters video control
 * @property {Number} restartProgress 1-100 percentage start time
 * @property {String} ajaxUrl Send percentage progress via ajax in JSON format
 * @property {String} ajaxId video id
 * @property {Number} ajaxIntervalForUpdate interval for ajax updating
 */

/**
 * video progress param
* @typedef {Object} VIDEO_PROGRESS
* @property {String} VideoId  Video Id
* @property {Number} Progress  Time progress (seconds)
* @property {Date} LastViewed  Last viewed
*/

/**
 * chpaters video options
 * @typedef {Object} CHAPTERS_VIDEO_OPTIONS ChaptersVideo_JS parameters
 * @property {string} selector query selector
 * @property {Function} fnUpdateCurrentTime set time function from VideoPlayer_JS class
 * @property {Boolean} isMandatory if video mandatory, disable click on chapters
 */
/**
 * Video player component similar to youtube player
 */
//TODO: add store progress
class VideoPlayer_JS {
  /**
   * Constructor
   * @param {VIDEO_PLAYER_OPTIONS} params
   */
  constructor(params) {
    const options = {
      selector: "div.video-container",
      mandatory: null,
      numberOfPreviewImages: 0,
      previewImagesPathAndFileName: '',
      chaptersVideoSelector: '',
      restartProgress: 0,
      ajaxId : '',
      ajaxUrl: '',
      ajaxIntervalForUpdate : 20000,
      ...params,
    };

    /**
     * player options
     * @type {VIDEO_PLAYER_OPTIONS}
     */
    this.Options = {
      ...options,
    };
    /**
     * Player buttons
     * @type {PLAYER_BUTTONS}
     */
    this.Buttons = {
      playPauseBtn: null,
      theaterBtn: null,
      fullScreenBtn: null,
      minPlayerBtn: null,
      muteBtn: null,
      captionsBtn: null,
      speedBtn: null,
    };
    /**
     * player containers
     * @type {PLAYER_CONTAINERS}
     */
    this.Containers = {
      volumeSlider: null,
      currentTimeElement: null,
      totalTimeElement: null,
      timeLineContainer: null,
      previewImg: null,
      thumbnailImg: null,
      thumbIndicator: null,
      videoContainer: null,
      playIcon: null,
    };
    /**
     * Chapters video functionality
     * @type {ChaptersVideo_JS}
     */
    this.ChaptersVideo = null;
    /**
     * restart progress
     * @type {Number}
     */
    this.RestartProgress = 0;
    /**
     * set first time to play
     * @type {Boolean}
     */
    this.firstPlay = false;
    /**
     * set timt out handler
     * @type {Number}
     */
    this.timeOutHandler = 0;

    this.init();
  }
  // #region METHODS
  /**
   * initialize control
   */
  init() {
    if (!this.Options.selector) {
      throw new Error("selector is an empty string.");
    }
    /** 
    * @type {HTMLDivElement}
    */
    const videoContainer =
      typeof this.Options.selector === "string"
        ? document.querySelector(this.Options.selector)
        : this.Options.selector instanceof HTMLElement
        ? this.Options.selector
        : null;

    if (!videoContainer) {
      throw new Error(
        `Cannot find the video container. Current Selector: ${this.Options.selector}`
      );
    }
    //reload options
    if (typeof this.Options.mandatory !== "boolean") {
      const _isMandatory = videoContainer.dataset?.mandatory;
      if (_isMandatory) {
        this.Options.mandatory = _isMandatory.toLowerCase() === "true";
      } else {
        this.Options.mandatory = false;
      }
    }

    if (isNaN(this.Options.numberOfPreviewImages) || Math.abs(parseInt(this.Options.numberOfPreviewImages)) === 0) {
      let _numberPreviewImages = videoContainer.dataset.numberPreviewImages;
      if (isNaN(_numberPreviewImages)) {
        this.Options.numberOfPreviewImages = 1;
      } else {
        _numberPreviewImages = Math.abs(parseInt(_numberPreviewImages));
        this.Options.numberOfPreviewImages = _numberPreviewImages === 0 ? 1 : _numberPreviewImages;
      }
    } else {
      this.Options.numberOfPreviewImages = Math.abs(parseInt(this.Options.numberOfPreviewImages));
    }

    if (!this.Options.previewImagesPathAndFileName) {
      this.Options.previewImagesPathAndFileName = videoContainer.dataset.pathAndFilename;
    }

    if(!this.Options.chaptersVideoSelector){
        this.Options.chaptersVideoSelector = videoContainer.dataset?.videoChaptersList;
    }

    if (!this.Options.ajaxId) {
      this.Options.ajaxId = videoContainer.dataset?.ajaxId;
    } 
    if (!this.Options.ajaxUrl) {
      this.Options.ajaxUrl = videoContainer.dataset?.ajaxUrl;
    }  
    // get containers
    this.Containers.videoContainer = videoContainer;

    this.Containers.volumeSlider = videoContainer.querySelector(".volume-slider");
    this.Containers.currentTimeElement = videoContainer.querySelector(".current-time");
    this.Containers.totalTimeElement = videoContainer.querySelector(".total-time");
    this.Containers.timeLineContainer = videoContainer.querySelector(".timeline-container");
    this.Containers.previewImg = videoContainer.querySelector(".preview-img");
    this.Containers.thumbnailImg = videoContainer.querySelector(".thumbnail-img");
    this.Containers.thumbIndicator = videoContainer.querySelector(".thumb-indicator");
    this.Containers.playIcon = videoContainer.querySelector(".playIcon");

    // get buttons
    this.Buttons.playPauseBtn = videoContainer.querySelector(".play-pause-btn");
    this.Buttons.theaterBtn = videoContainer.querySelector(".theater-btn");
    this.Buttons.fullScreenBtn = videoContainer.querySelector(".full-screen-btn");
    this.Buttons.minPlayerBtn = videoContainer.querySelector(".mini-player-btn");
    this.Buttons.muteBtn = videoContainer.querySelector(".mute-btn");
    this.Buttons.captionsBtn = videoContainer.querySelector(".captions-btn");
    this.Buttons.speedBtn = videoContainer.querySelector(".speed-btn");
    this.Buttons.replayBtn = videoContainer.querySelector(".replay-btn");

    // get video elements
    this._video = videoContainer.querySelector(".videoplayer");
    this._captions = this.Video.textTracks[0];

    !!this._captions && (this.Captions.mode = "hidden"); //hide subtititles

    // keydown events
    document.addEventListener("keydown", (e) => {
      const _tagName = document.activeElement.tagName.toLowerCase();

      if (_tagName === "input") {
        return;
      }

      switch (e.key.toLowerCase()) {
        case " ":
          if (_tagName === "button") {
            return;
          }
          e.preventDefault();
        case "k":
          this.togglePlayPause();
          break;
        case "f":
          !this.Options.mandatory && this.toggleFullScreenMode();
          break;
        case "t":
          this.toggleTheaterMode();
          break;
        case "i":
          this.toggleMiniPlayerMode();
          break;
        case "m":
          this.toggleMute();
          break;
        case "arrowleft":
        case "j":
          this.skip(-5);
          break;
        case "arrowright":
        case "l":
          !this.Options.mandatory && this.skip(5);
          break;
        case "c":
          !!this._captions && this.toggleCaptions();
          break;
      }
    });
    // Click events
    this.Buttons.playPauseBtn.addEventListener("click",this.togglePlayPause.bind(this),false);
    this.Buttons.theaterBtn.addEventListener("click",this.toggleTheaterMode.bind(this),false);
    this.Buttons.fullScreenBtn.addEventListener("click",this.toggleFullScreenMode.bind(this),false);
    this.Buttons.minPlayerBtn.addEventListener("click",this.toggleMiniPlayerMode.bind(this),false);
    this.Buttons.muteBtn.addEventListener("click",this.toggleMute.bind(this),false);
    this.Buttons.speedBtn.addEventListener("click", this.changePlaybackSpeed.bind(this), false);
    this.Buttons.replayBtn.addEventListener("click", this.replayVideo.bind(this),false);
    this.Video.addEventListener("click",this.togglePlayPause.bind(this),false);

    if (this._captions) {
        this.Buttons.captionsBtn.addEventListener( "click",this.toggleCaptions.bind(this),false);
    }
    else{
        this.Buttons.captionsBtn.style.visibility ="hidden";
    }
    // Input events
    this.Containers.volumeSlider.addEventListener("input",this.movingSliderVolume.bind(this),false);
    //mouse events
    if (this.Options.mandatory) {
      this.Buttons.fullScreenBtn.style.display = 'none';
    }
    else{
      this.Containers.timeLineContainer.addEventListener("mousemove",this.handleTimelineUpdate.bind(this),false );
      this.Containers.timeLineContainer.addEventListener("mousedown",this.toggleScrubbing.bind(this),false);
      document.addEventListener("mouseup",this.updateScrubbing.bind(this),false);
      document.addEventListener("mousemove", this.updateTimeLineIfScrubbing.bind(this), false);
    }
    //video events
    const playIconDiv = this.Containers.playIcon;
    this.Video.addEventListener("play",  () => (videoContainer.classList.remove("paused"),playIconDiv.classList.add("active")),false);
    this.Video.addEventListener("pause", () => (videoContainer.classList.add("paused"),playIconDiv.classList.remove("active")),false);
    this.Video.addEventListener("enterpictureinpicture", () => videoContainer.classList.add("mini-player"), false );
    this.Video.addEventListener("leavepictureinpicture", () => videoContainer.classList.remove("mini-player"),false);
    this.Video.addEventListener("volumechange",this.changingVolume.bind(this),false);
    this.Video.addEventListener("ended",this.ended.bind(this),false);
    //display total time
    this.Video.addEventListener("loadeddata", this.loadeddata.bind(this), false);
    //current time
    this.Video.addEventListener("timeupdate",this.timeUpdate.bind(this),false);

    //minimized/maximized
    document.addEventListener("visibilitychange",this.toggleMinimizedMaximizedWindow.bind(this), false);

    // set restart progress
    const _restartProgress = isNaN(videoContainer.dataset?.restartProgress) ? 0 : parseInt(videoContainer.dataset?.restartProgress),
      _paramRestartProgress = isNaN(this.Options.restartProgress) ? 0 : parseInt(this.Options.restartProgress);

    this.RestartProgress = Math.max(_restartProgress, _paramRestartProgress);
    
    if (this.RestartProgress < 0 || this.RestartProgress > 99) {
      this.RestartProgress = 0;
    }
    else {
      this.RestartProgress = this.RestartProgress / 100;
    }

    // set chapters video enable
    if (this.Options.chaptersVideoSelector) {
      this.ChaptersVideo = new ChaptersVideo_JS({
        selector: this.Options.chaptersVideoSelector,
        fnUpdateCurrentTime: this.setTime.bind(this),
        isMandatory : this.Options.mandatory
      })
    } 
  }
  /**
   * ended video event 
   */
  ended() {
    if (this.ChaptersVideo) {
      this.ChaptersVideo.ended();
    }
    clearTimeout(this.timeOutHandler);
    this.updateProgress(true);
  }
  /**
   * replay video
   */
  replayVideo() {
    this.Video.currentTime = 0;
  }
  /**
   * loadeddata event
   */
  loadeddata() {
      this.Containers.totalTimeElement.textContent = Formatter_JS.formatDuration(this.Video.duration)
      !!this.ChaptersVideo && this.ChaptersVideo.tryToMarkChapter(this.Video.duration * this.RestartProgress)
  }
  /**
   * enable/disible miniplayer Mode if Minimized or Maximized Window
   */
  toggleMinimizedMaximizedWindow() {
    const videoContainer = this.Containers.videoContainer;
    
    if (document.visibilityState === 'hidden') {
      if (videoContainer.classList.contains("mini-player")) {
        return;
      }
      this.toggleMiniPlayerMode();
    }
    else {
      if (videoContainer.classList.contains("mini-player")) {
        this.toggleMiniPlayerMode();
      }
    }
  }
  /**
   * toggle play pause
   */
  togglePlayPause() {
    const _video = this.Video,
      _ajax_Interval = this.Options.ajaxIntervalForUpdate;
    if (!this.firstPlay && this.RestartProgress > 0) {
      this.Video.currentTime = (this.Video.duration * this.RestartProgress)
    }
    this.firstPlay = true;
    
    if (_video.paused) {
      _video.play();
      this.timeOutHandler = setTimeout(this.updateProgress.bind(this), _ajax_Interval)
    } else {
      _video.pause();
      clearTimeout(this.timeOutHandler);
    }
  }
  /**
   * Toggle Theater Mode
   */
  toggleTheaterMode() {
    this.Containers.videoContainer.classList.toggle("theater");
  }
  /**
   * toggle FullScreen Mode
   */
  toggleFullScreenMode() {
    if (document.fullscreenElement == null) {
      this.Video.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  }
  /**
   * toggle MiniPlayer Mode
   */
  toggleMiniPlayerMode() {
    const videoContainer = this.Containers.videoContainer;
      if (videoContainer.classList.contains("mini-player")) {
        document.exitPictureInPicture();
      } else {
        this.Video.requestPictureInPicture()
          .catch(e => { });
      }
  }
  /**
   * mute video
   */
  toggleMute() {
    const _video = this.Video;
    _video.muted = !_video.muted;
  }
  /**
   * moving slider to change volume (input event)
   * @param {InputEvent} e event object
   */
  movingSliderVolume(e) {
    const _video = this.Video;
    _video.volume = e.currentTarget.value;
    _video.muted = e.target.value === 0;
  }
  /**
   * Update volume slider and volume icons values (volumechange event)
   */
  changingVolume() {
    const volumeSlider = this.Containers.volumeSlider;
    const _video = this.Video;

    volumeSlider.value = _video.volume;

    let volumeLevel;
    if (_video.muted || _video.volume === 0) {
      volumeSlider.value = 0;
      volumeLevel = "muted";
    } else if (_video.volume >= 0.5) {
      volumeLevel = "high";
    } else {
      volumeLevel = "low";
    }
    this.Containers.videoContainer.dataset.volumeLevel = volumeLevel;
  }
  /**
   * prints current time in the slider control
   */
  timeUpdate() {
    const _video = this.Video,
      _percent = _video.currentTime / _video.duration;
    this.Containers.currentTimeElement.textContent = Formatter_JS.formatDuration(_video.currentTime);
    this.Containers.timeLineContainer.style.setProperty(
      "--progress-position",
      _percent
    );
    if (!this.Containers.totalTimeElement.textContent) {
      this.Containers.totalTimeElement.textContent = Formatter_JS.formatDuration(this.Video.duration)
    }
    if (this.ChaptersVideo) {
      this.ChaptersVideo.tryToMarkChapter(_video.currentTime);
    }
  }
  /**
   * increse the current time to head or tail
   * @param {Number} duration seconds to add or subtract
   */
  skip(duration) {
    this.Video.currentTime += duration;
  }
  /**
   * Enable/Disable subtitles
   */
  toggleCaptions() {
    const _captions = this.Captions,
      _isHidden = _captions.mode === "hidden";
    _captions.mode = _isHidden ? "showing" : "hidden";
    this.Containers.videoContainer.classList.toggle("captions", _isHidden);
  }
  /**
   * change play speed (click on speed button)
   */
  changePlaybackSpeed() {
    let newPlaybackRate = this.Video.playbackRate + 0.25;
    if (newPlaybackRate > 2) {
      newPlaybackRate = 0.25;
    }
    this.Video.playbackRate = newPlaybackRate;
    this.Buttons.speedBtn.textContent = `${newPlaybackRate}x`;
  }
  /**
   *Update Time line
   * @param {MouseEvent} e
   */
  handleTimelineUpdate(e) {
    const _timeLineContainer = this.Containers.timeLineContainer,
      _pathAndFileName = this.Options.previewImagesPathAndFileName,
      _percent = this.getTimelineProgress(e),
      _previewImgNumber = Math.max(
        1,
        Math.floor(
          (_percent * this.Video.duration) / this.Options.numberOfPreviewImages
        )
      ),
      _previewImgSrc = `${_pathAndFileName}${_previewImgNumber}.jpg`;

    !!_pathAndFileName && (this.Containers.previewImg.src = _previewImgSrc);
    _timeLineContainer.style.setProperty("--preview-position", _percent);

    if (this._isScrubbing) {
      e.preventDefault();
      !!_pathAndFileName && (this.Containers.thumbnailImg.src = _previewImgSrc);
      _timeLineContainer.style.setProperty("--progress-position", _percent);
    }
  }
  /**
   * Update video progress based on scrubbing
   * @param {MouseEvent} e
   */
  toggleScrubbing(e) {
    const _percent = this.getTimelineProgress(e),
      _video = this.Video;

    this._isScrubbing = (e.buttons & 1) === 1;
    this.Containers.videoContainer.classList.toggle(
      "scrubbing",
      this._isScrubbing
    );

    if (this._isScrubbing) {
      _video.pause();
    } else {
      _video.currentTime = _percent * _video.duration;
      _video.play();
    }
    this.handleTimelineUpdate(e);
  }
  /**
   * Update video progress based on scrubbing
   * @param {MouseEvent} e
   */
  updateScrubbing(e) {
    if (this._isScrubbing) {
      this.toggleScrubbing(e);
    }
  }
  /**
   * Update timeline based on scrubbing
   * @param {MouseEvent} e
   */
  updateTimeLineIfScrubbing(e) {
    if (this._isScrubbing) {
      this.handleTimelineUpdate(e);
    }
  }
  /**
   * get timeline progress using mouse movements in the axis "x"
   * @param {MouseEvent} e mouse event object
   * @returns {Number} decimal percent
   */
  getTimelineProgress(e) {
    const _rect = this.Containers.timeLineContainer.getBoundingClientRect();
    return Math.min(Math.max(0, e.x - _rect.x), _rect.width) / _rect.width;
  }
  /**
   * Set current video time
   * @param {Number} seconds
   */
  setTime(seconds) {
    this.Video.currentTime = seconds;
  }
  /**
   * Ajax updating progress
   * @param {boolean} [isEnded=false]  video is ended
   */
  updateProgress(isEnded = false) {
    if (!this.Options.ajaxId && !this.Options.ajaxUrl) {
      return;
    }
    const _progress = isEnded ? 100 : parseInt(Math.ceil(this.Video.currentTime / this.Video.duration * 100)),
      _this = this,
      _ajax_Interval = this.Options.ajaxIntervalForUpdate;
    /**
     * @type {VIDEO_PROGRESS}
     */
    const _params = {
      VideoId: this.Options.ajaxId,
      Progress: _progress,
      LastViewed: new Date()
    }
    AJAXCommunications_JS.AsyncPostJSON(this.Options.ajaxUrl, _params)
      .then(
        /**
         * @param {AJAX_RESPONSE} r 
         */
        r => {
          if (r.isRedirected) {
            console.error('Cannot update video progress. See the response with Dev Tools in the Network Tab.')
          }
          else if (!r.data?.IsSuccessful) {
            (!r.data?.Reason || r.data.Reason.length == 0) && console.error('Cannot update video progress.', r.data?.Response);
            for (let reason of r.data.Reason) {
              console.error('Cannot update video progress:', reason);
            }
          }
        })
      .catch(
        /**
         * @param {AJAX_ERROR} e 
         */
        e => {
          console.error('Cannot update video progress:', ` message: ${e.message}`, ` stack: ${e.stack}`);
          console.error('Cannot update video progress:', ` status: ${e.status}`, ` status text: ${e.statusText}`);
        })
      .finally(() => {
        if (isEnded) {
          clearTimeout(_this.timeOutHandler);
          window.location.reload(); 
          return;
        }

        if(!_this.Video.paused)
          _this.timeOutHandler = setTimeout(_this.updateProgress.bind(_this), _ajax_Interval);
      });
  }
  // #endregion
  // #region PROPERTIES
  /**
   * @type {HTMLVideoElement}
   */
  _video = null;
  /**
   * @type {TextTrack}
   */
  _captions = null;
  /**
   * Is scrubbing
   * @type {boolean}
   */
  _isScrubbing = false;
  /**
   * getter Video object
   * @type {HTMLVideoElement}
   */
  get Video() {
    return this._video;
  }
  /**
   * getter captions object
   * @type {TextTrack}
   */
  get Captions() {
    return this._captions;
  }
  // #endregion
  static activeAllVideos() {
    document
      .querySelectorAll("div.video-container")
      .forEach((v) => new VideoPlayer_JS());
  }
}
/**
 * ChaptersVideo functionality
 */
class ChaptersVideo_JS {
    /**
     * Constructor
     * @param {CHAPTERS_VIDEO_OPTIONS} params 
     */
    constructor(params) {
      const options = {
        selector: '',
        fnUpdateCurrentTime: null,
        isMandatory : false,
        ...params,
      };
      /**
       * @type {CHAPTERS_VIDEO_OPTIONS}
       */
      this.Options = options;
      /**
       * video chapters container
       * @type {HTMLDivElement}
       */
      this.Container = null;
      /**
       * video chapter Html elements
       * @type {Array.<HTMLElement>}
       */
      this.Chapters = null;
  
      this.init();
    }
    /**
     * initialized method
     */
    init() {
      if (!this.Options?.selector) {
        console.log("Missing video chapter container Selector");
        return;
      }
  
      this.Container = document.querySelector(this.Options.selector);

      if (!this.Container) {
        console.log("Invalid video Chapters selector");
        return;
      }

      const _chapters = this.Container.querySelectorAll('[role="button"]');

      if (!_chapters || _chapters.length === 0) {
        console.log("No chapters elementos were found.");
        return;
      }

      this.Chapters = Array.from(_chapters);

      let previous;
      for (let index = this.Chapters.length - 1; index >= 0; index--) {
        const c = this.Chapters[index],
          _startTime = c.dataset?.startTime,
          _htmlTimer = c.querySelector('.vid-subtit');
        if (!this.Options.isMandatory) {
          c.addEventListener('click', this.setCurrentTime.bind(this), false);
        }
        if (_startTime && _htmlTimer) {
          _htmlTimer.textContent = Formatter_JS.formatDuration(parseInt(_startTime));
        }
        if (previous) {
          c.dataset.endTime = previous;
        }
        previous = isNaN(c.dataset?.startTime) ? 0 : parseInt(c.dataset.startTime);
      }
    }
    /**
     * set current time in the video
     * @param {Event} e click event
     */
  setCurrentTime(e) {
    const _startTime = e.currentTarget.dataset?.startTime;
    if (isNaN(_startTime)) {
      return;
    }
    if (typeof this.Options.fnUpdateCurrentTime === 'function') {
      this.Options.fnUpdateCurrentTime(parseInt(_startTime))
    }
  }
  /**
   * try to mark chapter as finished it
   * @param {Number} currentTime
   */
  tryToMarkChapter(currentTime) {
    !!this.Chapters && this.Chapters.forEach(c => {
      if (c.dataset?.endTime) {
        const _endtime = parseInt(c.dataset.endTime);
        if (currentTime > _endtime) {
          c.querySelector('.chapter-mark').classList.remove('d-none');
        }
      }
    });
  }
  /**
   * Mark final chapter
   */
  ended() {
    !!this.Chapters && this.Chapters[this.Chapters.length - 1].querySelector('.chapter-mark').classList.remove('d-none');
  }
}